import { Box } from '@mantine/core';
import { UniqueIdentifier } from '@vision/ui/interfaces';
import { omit } from '@vision/ui/utils';
import clsx from 'clsx';
import React, { CSSProperties, useEffect } from 'react';
import './DraggableListItem.css';

export interface RenderOptions<T = any> {
  data: T;
  disabled: boolean;
  dragOverlay: boolean;
  dragging: boolean;
  id: UniqueIdentifier;
  index: number;
  onRemove: VoidFunction;
  sorting: boolean;
}

export type ItemRenderType<T = any> = (options: RenderOptions<T>) => React.ReactNode;

export interface DraggableListItemProps<T = any> {
  children: ItemRenderType<T>;
  containerStyle?: CSSProperties;
  data: T;
  disabled?: boolean;
  dragOverlay?: boolean;
  dragging?: boolean;
  height?: number;
  id: UniqueIdentifier;
  index?: number;
  manualHandle?: boolean;
  onRemove?: VoidFunction;
  sorting?: boolean;
  style?: CSSProperties;
}

export const DraggableListItem = React.memo(
  React.forwardRef<HTMLDivElement, DraggableListItemProps>(
    (
      {
        children,
        containerStyle,
        data,
        disabled,
        dragOverlay,
        dragging,
        id,
        index,
        manualHandle,
        onRemove,
        sorting,
        style,
        ...props
      },
      ref,
    ) => {
      useEffect(() => {
        if (!dragOverlay) {
          return;
        }

        document.body.style.cursor = 'grabbing';

        return () => {
          document.body.style.cursor = '';
        };
      }, [dragOverlay]);

      return (
        <Box
          className={clsx('draggable-container', {
            sorting,
          })}
          style={
            {
              ...containerStyle,
            } as any
          }
          ref={ref}
        >
          <Box
            className={clsx('draggable', {
              dragging,
              dragOverlay,
              disabled,
            })}
            style={style}
            {...props}
            tabIndex={!manualHandle ? 0 : undefined}
          >
            {children({
              data: omit(data, 'sortId'),
              disabled,
              dragOverlay,
              dragging,
              id,
              index,
              onRemove,
              sorting,
            })}
          </Box>
        </Box>
      );
    },
  ),
);
